// src/api/schemas/tienda.ts — what a store owner reads about their own store.
//
// Every shape here comes from a user-scoped route the app already uses
// (`/stores/me`, `/users/me/listings`, `/orders?role=seller`,
// `/kohub/cases?role=seller`, `/wallet/payouts`), each reading only the
// caller's rows. The panel declares the few fields it prints and passes the
// rest through: a column the backend adds tomorrow must not fail the page.
import { z } from 'zod';

const Money = z.number().int();

/** `/stores/me` → `profile`: the owner's own store, or `null` before one exists. */
export const MyStore = z
  .object({
    id: z.string(),
    status: z.string(),
    storeName: z.string(),
    city: z.string(),
    rfc: z.string().nullish(),
    url: z.string().nullish(),
    instagram: z.string().nullish(),
    facebook: z.string().nullish(),
    pickupInstructions: z.string().nullish(),
    appliedAt: z.string().nullish(),
    approvedAt: z.string().nullish(),
    founderSince: z.string().nullish(),
    founderRevokedAt: z.string().nullish(),
    founderRevokedReason: z.string().nullish(),
  })
  .passthrough();
export type MyStore = z.infer<typeof MyStore>;

export const MyStoreResponse = z
  .object({
    /** The primary store (the oldest ACTIVE one), kept for the app in the field. */
    profile: MyStore.nullable(),
    /** ko-backend#1279 — every store of the owner, ACTIVE first; absent on an older backend. */
    stores: z.array(MyStore).optional(),
    /** The application's eligibility read; informative for a store that already exists. */
    eligibility: z
      .object({
        sellerVerified: z.boolean().optional(),
        activeListings: z.number().optional(),
        minActiveListings: z.number().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();
export type MyStoreResponse = z.infer<typeof MyStoreResponse>;

export const MyListing = z
  .object({
    id: z.string(),
    title: z.string(),
    status: z.string(),
    priceCents: Money,
    currency: z.string().optional(),
    rejectionReason: z.string().nullish(),
    createdAt: z.string(),
    photos: z.array(z.object({ cdnUrl: z.string().nullish() }).passthrough()).optional(),
  })
  .passthrough();
export type MyListing = z.infer<typeof MyListing>;

export const MyOrder = z
  .object({
    id: z.string(),
    role: z.string(),
    status: z.string(),
    totalCents: Money,
    listing: z.object({ id: z.string(), title: z.string() }).passthrough().nullish(),
    counterparty: z
      .object({ id: z.string(), displayName: z.string().nullish() })
      .passthrough()
      .nullish(),
    createdAt: z.string(),
  })
  .passthrough();
export type MyOrder = z.infer<typeof MyOrder>;

export const MyKoHubCase = z
  .object({
    id: z.string(),
    status: z.string(),
    role: z.string(),
    listing: z.object({ id: z.string(), title: z.string() }).passthrough(),
    agreedPriceCents: Money,
    requestedAt: z.string(),
    payDeadlineAt: z.string().nullish(),
    inboundDeadlineAt: z.string().nullish(),
  })
  .passthrough();
export type MyKoHubCase = z.infer<typeof MyKoHubCase>;

export const MyPayout = z
  .object({
    id: z.string(),
    amountCents: Money,
    currency: z.string(),
    status: z.string(),
    requestedAt: z.string(),
    paidAt: z.string().nullish(),
    failureReason: z.string().nullish(),
  })
  .passthrough();
export type MyPayout = z.infer<typeof MyPayout>;

/** Lists the app pages with a cursor; the panel reads one page of up to 50. */
export const Page = <S extends z.ZodTypeAny>(item: S) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullable().optional() }).passthrough();

/** The seller's words for a listing's status. An unknown one shows itself. */
export const LISTING_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Borrador',
  PENDING_REVIEW: 'En revisión',
  ACTIVE: 'Publicado',
  REJECTED: 'Rechazado',
  ARCHIVED: 'Archivado',
  SOLD: 'Vendido',
};
export const listingStatusLabel = (s: string) => LISTING_STATUS_LABELS[s] ?? s;

/** The seller's words for an order's status: what the buyer's money is doing. */
export const ORDER_STATUS_LABELS: Record<string, string> = {
  PENDING_PAYMENT: 'Pendiente de pago',
  PAID_HELD: 'Pagado · por enviar',
  SHIPPED: 'Enviado',
  DELIVERED: 'Entregado',
  CONFIRMED: 'Confirmado',
  RELEASED: 'Pago liberado',
  DISPUTED: 'En disputa',
  REPORTED: 'Reclamado',
  CANCELLED: 'Cancelado',
  REFUNDED: 'Reembolsado',
  CLOSED: 'Cerrado',
};
export const orderStatusLabel = (s: string) => ORDER_STATUS_LABELS[s] ?? s;

/** The orders a seller still has to act on: paid, not yet shipped. */
export const ORDER_WAITING_ON_SELLER = new Set(['PAID_HELD']);
/** The listings waiting on somebody: a reviewer, or the seller after a rejection. */
export const LISTING_IN_REVIEW = new Set(['PENDING_REVIEW']);
export const LISTING_REJECTED = new Set(['REJECTED']);
/** KO Hub cases where the next move is the seller's: ship the piece to KO. */
export const KOHUB_WAITING_ON_SELLER = new Set(['PAID']);
